// table-search.component.ts
import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { CurrentUserService } from '../../../core/services/Current-user/currentuser.service';
import { MenuInterface } from '../../../core/interfaces/user.interface';
import { extractAllTablesFromMenuRoots } from '../../utils/menu-tables.util';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-view-search-table',
  templateUrl: './view-search-table.component.html',
  styleUrl: './view-search-table.component.scss',
})
export class ViewSearchTableComponent {
  searchTerm: string = '';
  filteredTables: {
    id: number;
    title: string;
    link: string;
    type: number;
    class?: string;
    parentMenu: string;
    category?: string;
  }[] = [];
  allTables: {
    id: number;
    title: string;
    link: string;
    type: number;
    class?: string;
    parentMenu: string;
    category?: string;
  }[] = [];
  menuItems: MenuInterface[] = [];

  // Create a search text change subject for debouncing
  private searchTerms = new Subject<string>();
  private searchSubscription: Subscription;

  // Create an index for faster searching
  private searchIndex: Map<string, number[]> | any = new Map();

  // Maximum number of results to display
  private readonly MAX_RESULTS = 50;

  constructor(
    private router: Router,
    private currentUserService: CurrentUserService,
  ) {}

  ngOnInit(): void {
    this.currentUserService.menuReady$.subscribe(() => {
      this.loadAllTables();
      this.buildSearchIndex();
    });

    // Subscribe to search terms with debounce
    this.searchSubscription = this.searchTerms
      .pipe(
        debounceTime(200), // Wait for 200ms pause in events
        distinctUntilChanged(), // Only emit if value changed
      )
      .subscribe((term) => {
        this.performSearch(term);
      });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions when component is destroyed
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  /**
   * Extract all tables from menuItems recursively
   */
  loadAllTables(): void {
    this.menuItems = this.currentUserService.user.menu;
    this.allTables = extractAllTablesFromMenuRoots(this.menuItems);
  }

  /**
   * Build a search index for faster search
   */
  private buildSearchIndex(): void {
    this.allTables.forEach((table, index) => {
      // Split both title and category into tokens (words)
      const titleTokens = table.title.toLowerCase().split(/\s+/);
      const categoryTokens = (table.category || '').toLowerCase().split(/\s+/);

      // Index title tokens
      titleTokens.forEach((token) => {
        if (!this.searchIndex.has(token)) {
          this.searchIndex.set(token, []);
        }
        this.searchIndex.get(token).push(index);
      });

      // Index category tokens
      categoryTokens.forEach((token) => {
        if (!this.searchIndex.has(token)) {
          this.searchIndex.set(token, []);
        }
        if (!this.searchIndex.get(token).includes(index)) {
          this.searchIndex.get(token).push(index);
        }
      });

      // Index prefixes for both title and category
      [...titleTokens, ...categoryTokens].forEach((token) => {
        for (let i = 2; i < token.length; i++) {
          const prefix = token.substring(0, i);
          if (!this.searchIndex.has(prefix)) {
            this.searchIndex.set(prefix, []);
          }
          if (!this.searchIndex.get(prefix).includes(index)) {
            this.searchIndex.get(prefix).push(index);
          }
        }
      });
    });
  }

  /**
   * Trigger search when input changes
   */
  onSearch(): void {
    // Push the current search term to the subject
    this.searchTerms.next(this.searchTerm);
  }

  /**
   * Perform the actual search using the search index
   */
  private performSearch(term: string): void {
    if (!term || term.trim() === '') {
      this.filteredTables = [];
      return;
    }

    const cleanTerm = term.toLowerCase().trim();

    // If the term is short, we might need fallback to full scan
    if (cleanTerm.length < 2) {
      // For very short terms, limit to exact matches only
      this.filteredTables = this.allTables
        .filter(
          (table) =>
            table.title.toLowerCase().startsWith(cleanTerm) ||
            (table.category || '').toLowerCase().startsWith(cleanTerm),
        )
        .slice(0, this.MAX_RESULTS);
      return;
    }

    // Split search term into tokens
    const searchTokens = cleanTerm.split(/\s+/);

    // Get potential matches using the index
    const matchedIndices = new Set<number>();

    searchTokens.forEach((token) => {
      // Look for exact token match first
      if (this.searchIndex.has(token)) {
        this.searchIndex.get(token).forEach((idx: any) => matchedIndices.add(idx));
      } else {
        // Look for prefix matches
        this.searchIndex.forEach((indices: any, indexedToken: any) => {
          if (indexedToken.startsWith(token)) {
            indices.forEach((idx: any) => matchedIndices.add(idx));
          }
        });
      }
    });

    // Convert matched indices to actual table objects
    const matchedTables = Array.from(matchedIndices)
      .map((idx) => this.allTables[idx])
      .filter((table) => {
        // Double-check that all tokens match (to handle multi-word searches)
        const title = table.title.toLowerCase();
        const category = (table.category || '').toLowerCase();
        return searchTokens.every((token) => title.includes(token) || category.includes(token));
      })
      .slice(0, this.MAX_RESULTS);

    // Sort results by relevance (exact matches first)
    this.filteredTables = matchedTables.sort((a, b) => {
      const aTitle = a.title.toLowerCase();
      const bTitle = b.title.toLowerCase();
      const aCategory = (a.category || '').toLowerCase();
      const bCategory = (b.category || '').toLowerCase();

      // Exact match gets highest priority
      const aExact = aTitle === cleanTerm || aCategory === cleanTerm;
      const bExact = bTitle === cleanTerm || bCategory === cleanTerm;
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;

      // Starts with gets second priority
      const aStarts = aTitle.startsWith(cleanTerm) || aCategory.startsWith(cleanTerm);
      const bStarts = bTitle.startsWith(cleanTerm) || bCategory.startsWith(cleanTerm);
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      // Finally sort by length (shorter titles first)
      return a.title.length - b.title.length;
    });
  }

  handleEnterKey(event: KeyboardEvent | any): void {
    event.preventDefault();

    if (this.filteredTables.length > 0) {
      this.navigateToTable(this.filteredTables[0]);
    }
  }

  navigateToTable(table: {
    id: number;
    title: string;
    link: string;
    type: number;
    class?: string;
  }): void {
    if (table.type === 0 || table.type === 2) {
      this.router.navigate(['/', 'dynamic', table.id, 'table']);
    } else if (table.type === 1) {
      this.router.navigate(['/', 'dynamic', table.id, 'chart']);
    } else {
      this.router.navigate(['/', 'dynamic', table.id, 'tableau']);
    }
    this.searchTerm = '';
    this.filteredTables = [];
  }

  editTable(
    table: {
      id: number;
      title: string;
      link: string;
      type: number;
      class?: string;
    },
    event: MouseEvent,
  ): void {
    event.stopPropagation();

    this.router.navigate(['/table', table.id, 'update']);

    this.searchTerm = '';
    this.filteredTables = [];
  }

  duplicateTable(
    table: {
      id: number;
      title: string;
      link: string;
      type: number;
      class?: string;
    },
    event: MouseEvent,
  ): void {
    event.stopPropagation();

    this.router.navigate(['/table', table.id, 'duplicate']);

    this.searchTerm = '';
    this.filteredTables = [];
  }
}
